import { Helmet } from "react-helmet-async";

type JsonLdObject = Record<string, unknown>;

type SEOProps = {
  title: string;
  description: string;
  canonical: string;
  ogType?: "website" | "article";
  image?: string;
  publishedTime?: string;
  modifiedTime?: string;
  /** Emits <meta name="robots" content="noindex, follow"> — for ad landing pages. */
  noindex?: boolean;
  /** JSON-LD schema objects — emitted at render time so they land in the
   *  prerendered HTML (visible to non-JS crawlers like GPTBot). Pass a single
   *  object or an array; falsy entries are dropped. */
  jsonLd?: JsonLdObject | (JsonLdObject | null | undefined | false)[];
};

function stripQuery(href: string): string {
  try {
    const u = new URL(href);
    return `${u.origin}${u.pathname}`;
  } catch {
    return href.split("?")[0].split("#")[0];
  }
}

/**
 * Render-time head manager built on react-helmet-async. Every meta tag and
 * every JSON-LD block returned here is emitted synchronously during React
 * rendering, so it appears in the SSR/prerendered HTML — visible to crawlers
 * that do not execute JavaScript (GPTBot, ClaudeBot, PerplexityBot,
 * social-preview bots).
 */
export default function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  image,
  publishedTime,
  modifiedTime,
  noindex,
  jsonLd,
}: SEOProps) {
  const cleanCanonical = stripQuery(canonical);
  const absImage = image
    ? image.startsWith("http")
      ? image
      : `https://finable.de${image}`
    : undefined;

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const validSchemas = schemas.filter(
    (s): s is JsonLdObject => Boolean(s) && typeof s === "object",
  );

  return (
    <Helmet>
      <html lang="de" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={cleanCanonical} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={cleanCanonical} />
      <meta property="og:type" content={ogType} />
      {absImage && <meta property="og:image" content={absImage} />}

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:card" content={absImage ? "summary_large_image" : "summary"} />
      {absImage && <meta name="twitter:image" content={absImage} />}

      {ogType === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {validSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
